import React from 'react';
import { Link } from 'react-router-dom';

const BuildDashboard: React.FC = () => {
  const dashboardItems = [
    {
      category: "🔧 Setup & Validation",
      description: "Automated setup processes with real-time validation",
      items: [
        {
          title: "Authentication Setup",
          description: "12-step database initialization and endpoint verification",
          path: "/checklists",
          icon: "🔐",
          estimatedTime: "2-3 min",
          steps: 12,
          status: "Production Ready"
        },
        {
          title: "Development Environment",
          description: "6-step full-stack environment validation",
          path: "/checklists",
          icon: "💻",
          estimatedTime: "1-2 min",
          steps: 6,
          status: "Production Ready"
        },
        {
          title: "File Upload System",
          description: "5-step R2 storage and worker verification",
          path: "/checklists",
          icon: "📁",
          estimatedTime: "1-2 min",
          steps: 5,
          status: "Production Ready"
        },
        {
          title: "Cloudflare AI Setup",
          description: "8-step AI integration testing and validation",
          path: "/checklists",
          icon: "🤖",
          estimatedTime: "2-3 min",
          steps: 8,
          status: "Production Ready"
        }
      ]
    },
    {
      category: "📚 Documentation",
      description: "Project documentation and feature tracking",
      items: [
        {
          title: "Changelog",
          description: "Complete feature history with metrics and version tracking",
          path: "/changelog",
          icon: "📝",
          estimatedTime: "Browse",
          steps: "4 versions",
          status: "Live"
        },
        {
          title: "Setup Guides",
          description: "Comprehensive documentation for authentication and development",
          path: "/docs",
          icon: "📖",
          estimatedTime: "Reference",
          steps: "Multiple",
          status: "Available"
        }
      ]
    },
    {
      category: "🎯 Core Features",
      description: "Main GoalYet application features",
      items: [
        {
          title: "Goal Achievement",
          description: "Hyper-focused goal interface with AI assistance",
          path: "/goalyet",
          icon: "🎯",
          estimatedTime: "Active",
          steps: "Eternal",
          status: "Live"
        },
        {
          title: "File Management",
          description: "Secure upload and storage with global CDN",
          path: "/files",
          icon: "📂",
          estimatedTime: "Active",
          steps: "Unlimited",
          status: "Live"
        },
        {
          title: "AI Flow Builder",
          description: "Visual conversation designer for AI interactions",
          path: "/flow-builder",
          icon: "🔄",
          estimatedTime: "Active",
          steps: "Custom",
          status: "Live"
        }
      ]
    }
  ];

  const metrics = {
    totalFeatures: 11,
    linesOfCode: "5,641+",
    setupTime: "<10 min",
    versions: 4,
    lastUpdate: "September 14, 2025"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🏗️ GoalYet Build Dashboard
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Complete overview of GoalYet's features, setup processes, and documentation.
            From automated checklists to comprehensive changelog tracking.
          </p>
        </div>

        {/* Metrics Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{metrics.totalFeatures}</div>
            <div className="text-sm text-gray-600">Features</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-green-600">{metrics.linesOfCode}</div>
            <div className="text-sm text-gray-600">Lines of Code</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-purple-600">{metrics.setupTime}</div>
            <div className="text-sm text-gray-600">Setup Time</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-orange-600">{metrics.versions}</div>
            <div className="text-sm text-gray-600">Versions</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-lg font-bold text-gray-600">Sep 14</div>
            <div className="text-sm text-gray-600">Last Update</div>
          </div>
        </div>

        {/* Dashboard Categories */}
        {dashboardItems.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-8">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {category.category}
              </h2>
              <p className="text-gray-600">{category.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.items.map((item, itemIndex) => (
                <Link
                  key={itemIndex}
                  to={item.path}
                  className="group bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 hover:border-blue-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-3xl">{item.icon}</div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'Production Ready'
                        ? 'bg-green-100 text-green-800'
                        : item.status === 'Live'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4">
                    {item.description}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center">
                      <span className="mr-1">⏱️</span>
                      {item.estimatedTime}
                    </span>
                    <span className="flex items-center">
                      <span className="mr-1">📋</span>
                      {item.steps} steps
                    </span>
                  </div>

                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700">
                    Open →
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        {/* Quick Actions */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">🚀 Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/checklists"
              className="flex flex-col items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <span className="text-2xl mb-2">✅</span>
              <span className="text-sm font-medium text-gray-900">Run Checklists</span>
            </Link>
            <Link
              to="/changelog"
              className="flex flex-col items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <span className="text-2xl mb-2">📝</span>
              <span className="text-sm font-medium text-gray-900">View Changelog</span>
            </Link>
            <Link
              to="/goalyet"
              className="flex flex-col items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <span className="text-2xl mb-2">🎯</span>
              <span className="text-sm font-medium text-gray-900">Create Goals</span>
            </Link>
            <Link
              to="/files"
              className="flex flex-col items-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <span className="text-2xl mb-2">📁</span>
              <span className="text-sm font-medium text-gray-900">Manage Files</span>
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>🤖 Built with Claude Code • Last updated: {metrics.lastUpdate}</p>
        </div>
      </div>
    </div>
  );
};

export default BuildDashboard;